
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  where,
  getDocs,
} from "firebase/firestore";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Avatar,
  Alert,
} from "@mui/material";
import { isLoggedIn } from "../session";
import BottomNav from "./BottomNav";
import Header3 from "./Header3";
import LogoLoader from "./LogoLoader";


function ChatBox() {
  // eslint-disable-next-line no-unused-vars
  const { placeId, placeUid, authUserId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [otherUserName, setOtherUserName] = useState("");
  const [otherUserImage, setOtherUserImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isContracted, setIsContracted] = useState(false);
  const [placePrice, setPlacePrice] = useState(0);
  const [paypalReady, setPaypalReady] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState({ type: "", message: "" });
  const [showCheckout, setShowCheckout] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [placeData, setPlaceData] = useState(null);

  // Mostrar pantalla de confirmación después del cobro
  useEffect(() => {
    if (isContracted && placeId) {
      const fetchPlace = async () => {
        const placeDocRef = doc(db, "places", placeId);
        const placeDoc = await getDoc(placeDocRef);
        if (placeDoc.exists()) {
          setPlaceData(placeDoc.data());
        }
      };
      fetchPlace();
      setShowConfirmation(true);
    }
  }, [isContracted, placeId]);
  const messagesEndRef = useRef(null);
  const paypalButtonRef = useRef(null);
  const checkoutSectionRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    const fetchAuthUser = async () => {
      const user = await isLoggedIn();
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setCurrentUser({
            ...user,
            displayName: userDoc.data().displayName,
            profileImageUrl: userDoc.data().profileImageUrl,
          });
        } else {
          console.error("User document not found");
        }
      } else {
        navigate("/login");
      }
    };
    fetchAuthUser();
  }, [navigate]);

  useEffect(() => {
    const fetchOtherUserInfo = async () => {
      if (placeUid) {
        const userDocRef = doc(db, "users", placeUid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setOtherUserName(userDoc.data().displayName);
          setOtherUserImage(userDoc.data().profileImageUrl);
        }
      }
    };
    fetchOtherUserInfo();
  }, [placeUid]);

  useEffect(() => {
    const fetchPlacePrice = async () => {
      if (!placeId) return;

      try {
        const placeDocRef = doc(db, "places", placeId);
        const placeDoc = await getDoc(placeDocRef);
        if (placeDoc.exists()) {
          const data = placeDoc.data();
          setPlacePrice(Number(data.price) || 0);
        }
      } catch (error) {
        console.error("Error fetching place price:", error);
      }
    };

    fetchPlacePrice();
  }, [placeId]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

    if (!clientId) {
      setPaymentStatus({
        type: "error",
        message:
          "Falta configurar PayPal. Define REACT_APP_PAYPAL_CLIENT_ID en el entorno.",
      });
      return;
    }

    if (window.paypal) {
      setPaypalReady(true);
      return;
    }

    const existingScript = document.querySelector("script[data-paypal-sdk='true']");
    if (existingScript) {
      existingScript.addEventListener("load", () => setPaypalReady(true));
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=capture`;
    script.async = true;
    script.dataset.paypalSdk = "true";

    script.onload = () => setPaypalReady(true);
    script.onerror = () => {
      setPaymentStatus({
        type: "error",
        message: "No se pudo cargar PayPal. Intenta recargar la página.",
      });
    };

    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (!currentUser || !placeId || !placeUid) return;

    const conversationId = [currentUser.uid, placeUid].sort().join("_");
    const messagesRef = collection(
      db,
      `conversations/${conversationId}/messages`
    );
    const q = query(messagesRef, orderBy("timestamp"));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const fetchedMessages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(fetchedMessages);
      setLoading(false);

      // Mark messages as read
      const unreadMessagesQuery = query(
        messagesRef,
        where("senderId", "==", placeUid),
        where("read", "==", false)
      );

      const unreadMessagesSnapshot = await getDocs(unreadMessagesQuery);

      unreadMessagesSnapshot.forEach(async (doc) => {
        await updateDoc(doc.ref, { read: true });
      });
    });

    // Check if the service is already contracted
    const checkContractStatus = async () => {
      const tripDocRef = doc(db, "trips", `${currentUser.uid}_${placeId}`);
      const tripDoc = await getDoc(tripDocRef);
      setIsContracted(tripDoc.exists());
    };
    checkContractStatus();

    return () => unsubscribe();
  }, [currentUser, placeId, placeUid]);

  // Función árbitro para filtrar datos personales
  const arbitroFiltraMensaje = (mensaje) => {
    // Expresiones para detectar teléfonos, emails, direcciones
    const patterns = [
      /\b\d{8,}\b/g, // Números largos (teléfonos)
      /\b\d{3,4}[-.\s]?\d{3,4}[-.\s]?\d{3,4}\b/g, // Teléfonos con separadores
      /[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+/g, // Emails
      /(calle|avenida|av\.|barrio|ciudad|cp|postal|domicilio|direccion|address)/gi, // Palabras de dirección
      /\b\d{5,6}\b/g, // Códigos postales
    ];
    let filtrado = mensaje;
    patterns.forEach((pat) => {
      filtrado = filtrado.replace(pat, "[bloqueado por árbitro]");
    });
    return filtrado;
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() === "" || !currentUser) return;

    // Filtrar mensaje antes de enviar
    const mensajeFiltrado = arbitroFiltraMensaje(newMessage);

    const conversationId = [currentUser.uid, placeUid].sort().join("_");
    const messagesRef = collection(
      db,
      `conversations/${conversationId}/messages`
    );

    try {
      await addDoc(messagesRef, {
        content: mensajeFiltrado,
        timestamp: new Date(),
        senderId: currentUser.uid,
        senderDisplayName: currentUser.displayName,
        read: false,
      });
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message: ", error);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSendMessage();
    }
  };

  const handleContractService = useCallback(async (paymentData = null) => {
    try {
      const tripDocRef = doc(db, "trips", `${currentUser.uid}_${placeId}`);
      await setDoc(tripDocRef, {
        uid: currentUser.uid,
        placeId: placeId,
        createdAt: new Date(),
        paymentMethod: paymentData ? "paypal" : "manual",
        paymentId: paymentData?.id || null,
        paymentStatus: paymentData?.status || null,
      });
      setIsContracted(true);
      setPaymentStatus({
        type: "success",
        message: "Pago aprobado. Servicio contratado con éxito.",
      });
    } catch (error) {
      console.error("Error contracting service:", error);
      setPaymentStatus({
        type: "error",
        message: "No se pudo registrar la contratación después del pago.",
      });
    }
  }, [currentUser, placeId]);

  useEffect(() => {
    if (
      !paypalReady ||
      !paypalButtonRef.current ||
      !window.paypal ||
      !showCheckout ||
      isContracted ||
      loading
    ) {
      return;
    }

    const amount = placePrice > 0 ? placePrice.toFixed(2) : "10.00";
    paypalButtonRef.current.innerHTML = "";

    window.paypal
      .Buttons({
        style: {
          layout: "vertical",
          shape: "rect",
          label: "paypal",
        },
        createOrder: (_data, actions) => {
          return actions.order.create({
            purchase_units: [
              {
                description: `Wandr - Contratación ${placeId}`,
                amount: {
                  value: amount,
                },
              },
            ],
          });
        },
        onApprove: async (_data, actions) => {
          const details = await actions.order.capture();
          await handleContractService(details);
        },
        onError: (err) => {
          console.error("PayPal error:", err);
          setPaymentStatus({
            type: "error",
            message: "PayPal devolvió un error. Intenta nuevamente.",
          });
        },
      })
      .render(paypalButtonRef.current);
  }, [paypalReady, showCheckout, isContracted, loading, placePrice, placeId, handleContractService]);

  const handleStartCheckout = () => {
    setShowCheckout(true);

    setTimeout(() => {
      checkoutSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
  };

  const formatTime = (timestamp) => {
    if (timestamp) {
      const date = timestamp.toDate();
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return "";
  };

  return (
    <Box
      sx={{
        backgroundColor: "#06130f",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header3 />
      <Container
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          mt: 10,
          mb: 8,
        }}
      >
        {showConfirmation && placeData ? (
          <Box sx={{ bgcolor: "#0f1715", p: 3, borderRadius: 3, mb: 3, border: "1px solid #16a34a", maxWidth: 500, mx: "auto" }}>
            <Typography variant="h5" sx={{ color: "#16a34a", mb: 2, fontWeight: 700, textAlign: "center" }}>
              ¡Reserva confirmada!
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, justifyContent: "center" }}>
              <Avatar src={otherUserImage} alt={otherUserName} sx={{ width: 64, height: 64, mr: 2, border: "2px solid #16a34a" }} />
              <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
                {otherUserName}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: "#9fd2b7", mb: 1, fontWeight: 500 }}>
              <b>Título:</b> {placeData.title}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9fd2b7", mb: 1 }}>
              <b>Descripción:</b> {placeData.description}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9fd2b7", mb: 1 }}>
              <b>Ubicación:</b> {placeData.address}, {placeData.city}, {placeData.province}, {placeData.country}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9fd2b7", mb: 1 }}>
              <b>Precio:</b> USD {placeData.price}
            </Typography>
            {placeData.images && placeData.images.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {placeData.images.map((img, idx) => (
                  <Avatar key={idx} src={img} alt="Imagen alojamiento" sx={{ width: 56, height: 56, mr: 1, mb: 1 }} />
                ))}
              </Box>
            )}
          </Box>
        ) : loading ? (
          <LogoLoader label="Cargando conversación..." minHeight={300} />
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              bgcolor: "rgba(25, 56, 43, 0.55)",
              borderRadius: 2,
              p: 2,
              mb: 2,
            }}
          >
            {messages.map((message) => (
              <Box
                key={message.id}
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems:
                    message.senderId === currentUser?.uid
                      ? "flex-end"
                      : "flex-start",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                  {message.senderId !== currentUser?.uid && (
                    <Avatar
                      src={otherUserImage}
                      alt={otherUserName}
                      sx={{ width: 24, height: 24, mr: 1 }}
                    />
                  )}
                  <Box
                    sx={{
                      bgcolor:
                        message.senderId === currentUser?.uid
                          ? "rgba(34, 197, 94, 0.25)"
                          : "rgba(19, 35, 29, 0.95)",
                      p: 1.2,
                      borderRadius: 1.5,
                      maxWidth: "70%",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 0.5,
                        fontWeight: "bold",
                        fontSize: "15px",
                        lineHeight: 1.2,
                        color:
                          message.senderId === currentUser?.uid
                            ? "#86ffd2"
                            : "#6ee7b7",
                      }}
                    >
                      {message.senderId === currentUser?.uid
                        ? currentUser.displayName
                        : otherUserName}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        display: "block",
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {message.content}
                    </Typography>
                  </Box>
                  {message.senderId === currentUser?.uid && (
                    <Avatar
                      src={currentUser.profileImageUrl}
                      alt={currentUser.displayName}
                      sx={{ width: 24, height: 24, ml: 1 }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, color: "white" }}
                >
                  {formatTime(message.timestamp)} -{" "}
                  {message.read ? "Leído" : "Enviado"}
                </Typography>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>
        )}
        <Box sx={{ display: "flex", mb: 2 }}>
          <TextField
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Escribe tu mensaje..."
            variant="outlined"
            fullWidth
            sx={{
              mr: 1,
              bgcolor: "rgba(10, 24, 18, 0.95)",
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "rgba(130, 245, 184, 0.35)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(130, 245, 184, 0.7)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#73f0ae",
                },
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendMessage}
            sx={{ height: 56 }}
          >
            Enviar
          </Button>
        </Box>
        {paymentStatus.message && (
          <Alert severity={paymentStatus.type === "success" ? "success" : "error"} sx={{ mb: 2 }}>
            {paymentStatus.message}
          </Alert>
        )}
        {isContracted ? (
          <Button variant="contained" disabled sx={{ bgcolor: "#16a34a", mb: 2 }}>
            Servicio Contratado
          </Button>
        ) : !showCheckout ? (
          <Button
            variant="contained"
            onClick={handleStartCheckout}
            sx={{ bgcolor: "#16a34a", mb: 2 }}
          >
            Contratar servicio
          </Button>
        ) : (
          <Box
            ref={checkoutSectionRef}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 3,
              bgcolor: "rgba(10, 24, 18, 0.9)",
              border: "1px solid rgba(130, 245, 184, 0.25)",
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 1.2, color: "#9fd2b7", fontWeight: 700, textAlign: "center" }}
            >
              Checkout seguro con PayPal {placePrice > 0 ? `(USD ${placePrice})` : ""}
            </Typography>
            <Box
              ref={paypalButtonRef}
              sx={{
                maxWidth: 430,
                mx: "auto",
                p: 1.2,
                borderRadius: 2,
                bgcolor: "#0f1715",
                border: "1px solid rgba(130, 245, 184, 0.15)",
                overflow: "hidden",
              }}
            />
            <Button
              variant="text"
              onClick={() => setShowCheckout(false)}
              sx={{ mt: 1.5, color: "#9fd2b7", display: "block", mx: "auto" }}
            >
              Cancelar checkout
            </Button>
          </Box>
        )}
      </Container>
      <BottomNav />
    </Box>
  );
};

export default ChatBox;

